import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, of, Subject, Subscription, switchMap } from 'rxjs';

import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { AuthService } from '../../../core/auth/auth.service';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { NotificationQueueService } from '../data-access/notification-queue.service';
import {
  NotificationQueueItem,
  NotificationQueuePage,
  NotificationQueueSort,
  NotificationQueueStatus,
} from '../data-access/notification-queue.models';
import { NotificationQueueTableComponent } from '../components/notification-queue-table.component';
import {
  FloatingActionFabComponent,
  ListFilterShortcutDirective,
  PageActionModalComponent,
  PageContextGuardComponent,
  PageShellComponent,
  PagedFeedComponent,
} from '@clinic-platform/ui-components';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { ClientsAdminService } from '../../clients/data-access/clients-admin.service';
import { ClientAdminItem } from '../../clients/data-access/clients-admin.models';

type NotificationQueueEntityFilter = '' | 'appointment' | 'appointment_change_request' | 'client_account_invite' | 'device_downtime' | 'reschedule_proposal' | 'specialist_downtime' | 'user_account_invite';
type NotificationQueueFilterState = {
  status: '' | NotificationQueueStatus;
  eventType: string;
  entityType: NotificationQueueEntityFilter;
  clientId: string;
  sort: NotificationQueueSort;
};

@Component({
  selector: 'app-notification-queue-page',
  standalone: true,
  imports: [
    CompanyContextRequiredComponent,
    FloatingActionFabComponent,
    ListFilterShortcutDirective,
    NotificationQueueTableComponent,
    PageActionModalComponent,
    PageContextGuardComponent,
    PageShellComponent,
    PagedFeedComponent,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  template: `
    <lib-page-shell
      libListFilterShortcut
      [libListFilterShortcut]="!companyContextMissing"
      (libListFilterShortcutOpen)="openFilterModal()"
    >
      <lib-page-context-guard [blocked]="companyContextMissing">
        <app-company-context-required />
      </lib-page-context-guard>

      @if (!companyContextMissing) {
        <lib-paged-feed
          [items]="items"
          [collectionTemplate]="queueTableTemplate"
          mode="manual"
          [hasMore]="hasMoreItems"
          [loading]="isLoading"
          [loadingMore]="isLoadingMore"
          [errorText]="errorText ? ((errorText | t) ?? '') : null"
          [loadingText]="'Loading notification queue...' | t"
          [emptyText]="'No notification queue jobs for current filters.' | t"
          [retryText]="'Retry' | t"
          [loadMoreText]="'Load more' | t"
          [loadingMoreText]="'Loading more...' | t"
          (loadMore)="loadMoreQueue()"
          (retry)="retryQueue()"
        />
      }

      <ng-template #queueTableTemplate let-feedItems>
        <app-notification-queue-table [items]="feedItems" />
      </ng-template>

      @if (!companyContextMissing) {
        <lib-floating-action-fab
          [menuTemplate]="queueActionsMenu"
          buttonAriaLabel="Open notification queue actions"
        />

        <ng-template #queueActionsMenu let-close="close">
          <div class="notification-queue-page__actions-menu">
            <button
              type="button"
              role="menuitem"
              class="action-menu__item"
              (click)="openFilterModal(); close()"
            >
              {{ 'Filters & Sort' | t }} (Ctrl+F)
            </button>
            @if (hasActiveFilters) {
              <button
                type="button"
                role="menuitem"
                class="action-menu__item"
                (click)="onResetFilters(); close()"
              >
                {{ 'Reset' | t }}
              </button>
            }
            <button
              type="button"
              role="menuitem"
              class="action-menu__item"
              (click)="retryQueue(); close()"
            >
              {{ 'Refresh' | t }}
            </button>
          </div>
        </ng-template>
      }

      @if (isFilterModalOpen) {
        <lib-page-action-modal
          [isOpen]="true"
          [title]="('Filters & Sort' | t) ?? 'Filters & Sort'"
          subtitle="Notification queue list controls"
          width="860px"
          [canSubmit]="true"
          (close)="closeFilterModal()"
          (submit)="onApplyFilters(); closeFilterModal()"
        >
          <form
            [formGroup]="filtersForm"
            class="notification-queue-page__modal-filters"
            (submit)="$event.preventDefault(); onApplyFilters(); closeFilterModal()"
          >
            <div class="notification-queue-page__filters-grid">
              <label class="notification-queue-page__filter">
                <span>{{ 'common.status' | t }}</span>
                <select
                  class="notification-queue-page__control"
                  formControlName="status"
                >
                  <option value="">{{ 'All' | t }}</option>
                  <option value="pending">{{ 'status.pending' | t }}</option>
                  <option value="processing">{{ 'status.processing' | t }}</option>
                  <option value="sent">{{ 'status.sent' | t }}</option>
                  <option value="failed">{{ 'status.failed' | t }}</option>
                </select>
              </label>

              <label class="notification-queue-page__filter">
                <span>{{ 'Event Type' | t }}</span>
                <select
                  class="notification-queue-page__control"
                  formControlName="eventType"
                >
                  <option value="">{{ 'All' | t }}</option>
                  @for (eventType of eventTypeOptions; track eventType) {
                    <option [value]="eventType">{{ humanizeKey(eventType) }}</option>
                  }
                </select>
              </label>

              <label class="notification-queue-page__filter">
                <span>{{ 'Entity Type' | t }}</span>
                <select
                  class="notification-queue-page__control"
                  formControlName="entityType"
                >
                  <option value="">{{ 'All' | t }}</option>
                  @for (entityType of entityTypeOptions; track entityType) {
                    <option [value]="entityType">{{ humanizeKey(entityType) }}</option>
                  }
                </select>
              </label>

              <label class="notification-queue-page__filter notification-queue-page__filter--client">
                <span>{{ 'Client' | t }}</span>
                <input
                  class="notification-queue-page__control"
                  formControlName="clientSearch"
                  list="notification-queue-client-options"
                  placeholder="Search client"
                  data-modal-primary-focus="true"
                  (input)="onClientSearchInput($event)"
                  (change)="onClientSearchCommitted($event)"
                  (keyup.enter)="onClientSearchCommitted($event)"
                />
                <datalist id="notification-queue-client-options">
                  @for (client of clientOptions; track client.id) {
                    <option [value]="clientOptionLabel(client)"></option>
                  }
                </datalist>
              </label>

              <label class="notification-queue-page__filter">
                <span>{{ 'appointments.filters.sort' | t }}</span>
                <select
                  class="notification-queue-page__control"
                  formControlName="sort"
                >
                  <option value="createdAtDesc">{{ 'notificationQueue.sort.createdAtDesc' | t }}</option>
                  <option value="createdAtAsc">{{ 'notificationQueue.sort.createdAtAsc' | t }}</option>
                  <option value="updatedAtDesc">{{ 'notificationQueue.sort.updatedAtDesc' | t }}</option>
                  <option value="updatedAtAsc">{{ 'notificationQueue.sort.updatedAtAsc' | t }}</option>
                </select>
              </label>
            </div>

            <div class="notification-queue-page__modal-actions">
              <button
                type="button"
                class="notification-queue-page__modal-secondary"
                (click)="onResetFilters(); closeFilterModal()"
              >
                {{ 'Reset' | t }}
              </button>
              <button type="submit" class="notification-queue-page__modal-primary">
                {{ 'Apply Filters' | t }}
              </button>
            </div>
          </form>
        </lib-page-action-modal>
      }
    </lib-page-shell>
  `,
  styles: [`
    .notification-queue-page__modal-filters {
      display: grid;
      gap: 16px;
    }

    .notification-queue-page__filters-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 16px;
    }

    .notification-queue-page__filter {
      display: grid;
      gap: 6px;
      color: #475569;
      font-size: 12px;
      font-weight: 700;
    }

    .notification-queue-page__filter--client {
      width: min(260px, 100%);
    }

    .notification-queue-page__control {
      min-width: 170px;
      height: 38px;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      background: #ffffff;
      color: #0f172a;
      padding: 0 10px;
      font: inherit;
      font-weight: 500;
      box-sizing: border-box;
    }

    .notification-queue-page__filter--client .notification-queue-page__control {
      width: 100%;
    }

    .notification-queue-page__modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      flex-wrap: wrap;
    }

    .notification-queue-page__modal-secondary,
    .notification-queue-page__modal-primary {
      min-height: 40px;
      border-radius: 10px;
      padding: 0 14px;
      font-weight: 600;
      cursor: pointer;
    }

    .notification-queue-page__modal-secondary {
      border: 1px solid #cbd5e1;
      background: #ffffff;
      color: #334155;
    }

    .notification-queue-page__modal-primary {
      border: none;
      background: #2563eb;
      color: #ffffff;
    }

    @media (max-width: 900px) {
      .notification-queue-page__filters-grid {
        grid-template-columns: 1fr;
      }

      .notification-queue-page__filter,
      .notification-queue-page__control {
        width: 100%;
      }
    }
  `]
})
export class NotificationQueuePageComponent implements OnInit, OnDestroy
{
  private static readonly PAGE_SIZE = 20;

  private readonly formBuilder = inject(FormBuilder);
  private readonly notificationQueueService = inject(NotificationQueueService);
  private readonly clientsAdminService = inject(ClientsAdminService);
  private readonly authService = inject(AuthService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly subscriptions = new Subscription();
  private readonly clientSearchTerms = new Subject<string>();

  protected items: NotificationQueueItem[] = [];
  protected isLoading = true;
  protected isLoadingMore = false;
  protected hasMoreItems = false;
  protected errorText: string | null = null;
  protected isSystemAdmin = false;
  protected nextCursor: string | null = null;
  protected isFilterModalOpen = false;
  protected clientOptions: ClientAdminItem[] = [];
  protected readonly filtersForm = this.formBuilder.group({
    status: ['' as '' | NotificationQueueStatus],
    eventType: [''],
    entityType: ['' as NotificationQueueEntityFilter],
    clientId: [''],
    clientSearch: [''],
    sort: ['createdAtDesc' as NotificationQueueSort],
  });
  private appliedFilters: NotificationQueueFilterState = {
    status: '',
    eventType: '',
    entityType: '',
    clientId: '',
    sort: 'createdAtDesc',
  };
  protected readonly eventTypeOptions = [
    'client_invite_sent',
    'staff_invite_sent',
    'appointment_created',
    'appointment_request_updated',
    'appointment_change_request_created',
    'appointment_change_request_approved',
    'appointment_change_request_rejected',
    'appointment_cancelled',
    'appointment_confirmed',
    'appointment_completed',
    'device_downtime_created',
    'specialist_downtime_created',
    'reschedule_proposal_generated',
    'reschedule_proposal_accepted',
    'reschedule_proposal_rejected',
    'reschedule_proposal_revoked',
  ];
  protected readonly entityTypeOptions: NotificationQueueEntityFilter[] = [
    'appointment',
    'appointment_change_request',
    'client_account_invite',
    'device_downtime',
    'reschedule_proposal',
    'specialist_downtime',
    'user_account_invite',
  ];

  public ngOnInit(): void
  {
    this.isSystemAdmin = this.authService.getCurrentRole() === 'system_admin';
    this.bindClientSearch();

    if (this.isSystemAdmin) {
      this.subscriptions.add(
        this.systemAdminContext.companyId$.pipe(distinctUntilChanged()).subscribe(() => {
          this.loadQueue({ append: false });
        }),
      );
      return;
    }

    this.loadQueue({ append: false });
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected loadMoreQueue(): void
  {
    if (this.isLoading || this.isLoadingMore || !this.hasMoreItems) {
      return;
    }

    this.loadQueue({ append: true });
  }

  protected retryQueue(): void
  {
    this.loadQueue({ append: false });
  }

  protected openFilterModal(): void
  {
    this.isFilterModalOpen = true;
  }

  protected closeFilterModal(): void
  {
    this.isFilterModalOpen = false;
  }

  protected onClientSearchInput(event: Event): void
  {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.filtersForm.patchValue({ clientId: '' }, { emitEvent: false });

    if (!value.trim()) {
      this.clientOptions = [];
      return;
    }

    this.clientSearchTerms.next(value);
  }

  protected onClientSearchCommitted(event: Event): void
  {
    this.normalizeClientSearch((event.target as HTMLInputElement | null)?.value ?? '');
  }

  protected onApplyFilters(): void
  {
    if (this.resolveClientBeforeApplying()) {
      return;
    }

    this.applyFiltersFromForm();
  }

  private applyFiltersFromForm(): void
  {
    const value = this.filtersForm.getRawValue();
    this.appliedFilters = {
      status: this.normalizeStatus(value.status),
      eventType: this.eventTypeOptions.includes(value.eventType?.trim() ?? '') ? value.eventType?.trim() ?? '' : '',
      entityType: this.normalizeEntityType(value.entityType),
      clientId: value.clientId?.trim() ?? '',
      sort: this.normalizeSort(value.sort),
    };
    this.loadQueue({ append: false });
  }

  protected onResetFilters(): void
  {
    this.filtersForm.reset({
      status: '',
      eventType: '',
      entityType: '',
      clientId: '',
      clientSearch: '',
      sort: 'createdAtDesc',
    });

    this.appliedFilters = {
      status: '',
      eventType: '',
      entityType: '',
      clientId: '',
      sort: 'createdAtDesc',
    };
    this.loadQueue({ append: false });
  }

  protected get hasActiveFilters(): boolean
  {
    return !!this.appliedFilters.status
      || !!this.appliedFilters.eventType
      || !!this.appliedFilters.entityType
      || !!this.appliedFilters.clientId
      || this.appliedFilters.sort !== 'createdAtDesc';
  }

  protected humanizeKey(value: string): string
  {
    return value
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  protected clientOptionLabel(client: ClientAdminItem): string
  {
    const parts = [
      client.fullName,
      client.email,
      client.phone,
    ]
      .map((part) => part?.trim() ?? '')
      .filter((part) => part.length > 0);

    return parts.length > 0 ? parts.join(' · ') : client.id;
  }

  private loadQueue(options: { append: boolean }): void
  {
    if (!this.hasRequiredCompanyContext()) {
      this.items = [];
      this.nextCursor = null;
      this.hasMoreItems = false;
      this.errorText = 'error.settingsCompanyContextRequired';
      this.isLoading = false;
      this.isLoadingMore = false;
      return;
    }

    if (options.append) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
      this.errorText = null;
      this.nextCursor = null;
      this.hasMoreItems = false;
    }

    this.notificationQueueService.getPage({
      limit: NotificationQueuePageComponent.PAGE_SIZE,
      cursor: options.append ? this.nextCursor : null,
      sort: this.appliedFilters.sort,
      filters: {
        status: this.appliedFilters.status || undefined,
        eventType: this.appliedFilters.eventType || undefined,
        entityType: this.appliedFilters.entityType || undefined,
        clientId: this.appliedFilters.clientId || undefined,
      },
    }).subscribe({
      next: (response: NotificationQueuePage) =>
      {
        this.items = options.append ? [...this.items, ...response.items] : response.items;
        this.nextCursor = response.nextCursor;
        this.hasMoreItems = response.hasMore;
        this.isLoading = false;
        this.isLoadingMore = false;
      },
      error: (error: unknown) =>
      {
        console.error('Failed to load notification queue', error);
        if (options.append) {
          this.errorText = null;
          this.isLoadingMore = false;
          return;
        }

        this.errorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenNotificationQueueList',
          fallback: 'Backend returned an error while loading notification queue.'
        });
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    });
  }

  private hasRequiredCompanyContext(): boolean {
    if (!this.isSystemAdmin) {
      return true;
    }

    return !!this.systemAdminContext.getCompanyId()?.trim();
  }

  protected get companyContextMissing(): boolean {
    return !this.hasRequiredCompanyContext();
  }

  private isQueueStatus(value: string): value is NotificationQueueStatus
  {
    return ['pending', 'processing', 'sent', 'failed'].includes(value);
  }

  private normalizeStatus(value: string | null | undefined): '' | NotificationQueueStatus
  {
    const normalized = value?.trim() ?? '';
    return this.isQueueStatus(normalized) ? normalized : '';
  }

  private normalizeSort(value: string | null | undefined): NotificationQueueSort
  {
    switch (value) {
      case 'createdAtAsc':
      case 'updatedAtDesc':
      case 'updatedAtAsc':
        return value;
      case 'createdAtDesc':
      default:
        return 'createdAtDesc';
    }
  }

  private isEntityFilter(value: string): value is NotificationQueueEntityFilter
  {
    return value === '' || this.entityTypeOptions.includes(value as NotificationQueueEntityFilter);
  }

  private normalizeEntityType(value: string | null | undefined): NotificationQueueEntityFilter
  {
    const normalized = value?.trim() ?? '';
    return this.isEntityFilter(normalized) ? normalized : '';
  }

  private bindClientSearch(): void
  {
    this.subscriptions.add(
      this.clientSearchTerms.pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((term) => {
          const query = term.trim();
          if (query.length < 2 || !this.hasRequiredCompanyContext()) {
            return of([] as ClientAdminItem[]);
          }

          return this.clientsAdminService.getPage({
            companyId: this.getContextCompanyId() ?? undefined,
            limit: 10,
            query,
          }).pipe(
            switchMap((page) => of(page.items)),
            catchError(() => of([] as ClientAdminItem[])),
          );
        }),
      ).subscribe((items) => {
        this.clientOptions = items;
        this.syncPendingClientSearchMatch();
      }),
    );
  }

  private syncPendingClientSearchMatch(): void
  {
    const value = this.filtersForm.getRawValue();
    const currentClientId = value.clientId?.trim() ?? '';
    const currentSearch = value.clientSearch?.trim() ?? '';
    if (currentClientId || !currentSearch) {
      return;
    }

    const matchedClient = this.findClientSearchMatch(currentSearch);
    if (!matchedClient) {
      return;
    }

    this.filtersForm.patchValue({
      clientId: matchedClient.id,
      clientSearch: this.clientOptionLabel(matchedClient),
    }, { emitEvent: false });
  }

  private resolveClientBeforeApplying(): boolean
  {
    const value = this.filtersForm.getRawValue();
    const currentClientId = value.clientId?.trim() ?? '';
    const currentSearch = value.clientSearch?.trim() ?? '';
    if (currentClientId) {
      return false;
    }

    if (!currentSearch) {
      this.filtersForm.patchValue({ clientId: '', clientSearch: '' }, { emitEvent: false });
      return false;
    }

    const localMatch = this.findClientSearchMatch(currentSearch);
    if (localMatch) {
      this.filtersForm.patchValue({
        clientId: localMatch.id,
        clientSearch: this.clientOptionLabel(localMatch),
      }, { emitEvent: false });
      return false;
    }

    if (currentSearch.length < 2 || this.isUuidLike(currentSearch)) {
      this.filtersForm.patchValue({
        clientId: this.isUuidLike(currentSearch) ? currentSearch : '',
      }, { emitEvent: false });
      return false;
    }

    this.clientsAdminService.getPage({
      companyId: this.getContextCompanyId() ?? undefined,
      limit: 10,
      query: currentSearch,
    }).subscribe({
      next: (page) => {
        this.clientOptions = page.items;
        const remoteMatch = this.findClientSearchMatch(currentSearch);
        this.filtersForm.patchValue({
          clientId: remoteMatch?.id ?? '',
          clientSearch: remoteMatch ? this.clientOptionLabel(remoteMatch) : currentSearch,
        }, { emitEvent: false });
        this.applyFiltersFromForm();
      },
      error: () => {
        this.filtersForm.patchValue({ clientId: '' }, { emitEvent: false });
        this.applyFiltersFromForm();
      },
    });

    return true;
  }

  private normalizeClientSearch(value: string): void
  {
    const normalized = value.trim();
    if (!normalized) {
      this.filtersForm.patchValue({ clientId: '', clientSearch: '' }, { emitEvent: false });
      return;
    }

    const matchedClient = this.findClientSearchMatch(normalized);

    if (matchedClient) {
      this.filtersForm.patchValue({
        clientId: matchedClient.id,
        clientSearch: this.clientOptionLabel(matchedClient),
      }, { emitEvent: false });
      return;
    }

    this.filtersForm.patchValue({
      clientId: this.isUuidLike(normalized) ? normalized : '',
    }, { emitEvent: false });
  }

  private findClientSearchMatch(value: string): ClientAdminItem | null
  {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return null;
    }

    const exactMatch = this.clientOptions.find((client) => {
      const candidates = [
        this.clientOptionLabel(client),
        client.id,
        client.fullName,
        client.email ?? '',
        client.phone ?? '',
      ].map((candidate) => candidate.trim().toLowerCase());

      return candidates.includes(normalized);
    });
    if (exactMatch) {
      return exactMatch;
    }

    const fuzzyMatches = this.clientOptions.filter((client) => {
      const candidates = [
        this.clientOptionLabel(client),
        client.fullName,
        client.email ?? '',
        client.phone ?? '',
      ].map((candidate) => candidate.trim().toLowerCase());

      return candidates.some((candidate) => candidate.includes(normalized));
    });

    return fuzzyMatches.length === 1 ? fuzzyMatches[0] : null;
  }

  private getContextCompanyId(): string | null
  {
    return this.isSystemAdmin
      ? this.systemAdminContext.getCompanyId()?.trim() || null
      : null;
  }

  private isUuidLike(value: string): boolean
  {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }
}
